<template>
  <div v-if="workflow" class="align-center d-flex justify-space-between ma-2 mr-n4 flex-nowrap top-workflow"
       style="gap: 8px;">
    <v-tooltip location="bottom" v-if="workflowStatusCompleted" width="400">
      <template v-slot:activator="{ props }">
        <v-btn class="mr-3" v-bind="props" variant="outlined" color="red" :ripple="false" prepend-icon="mds:lock">
          This workflow is locked
        </v-btn>
      </template>

      <span
      >
        This workflow is marked as completed.
        <span v-if="subscriptionAccessType=== 'FULL'">To unlock this workflow click the settings button to the right and go to general settings.</span>
        <span v-else>To unlock this workflow, please upgrade your subscription or contact support for further assistance.</span>
      </span
      >
    </v-tooltip>
    <span v-else style="flex: 1;"></span>

    <div class="d-flex justify-center flex-nowrap" style="flex: 1;">
      <div class="btn-group mr-2">
        <template v-if="!$store.state.workflow.isViewOnlyEnabled && subscriptionAccessType === 'FULL'">
          <v-tooltip location="top" open-delay="500">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :disabled="!previousVersionExists || workflowStatusCompleted"
                variant="tonal"
                icon="mds:replay"
                size="default"
                class="mr-2 captures-description"
                @click="undoDebounced(workflow)"
              />
            </template>
            <span
            >Undo: {{ changeNoteCurrent?.userRealName }}
              {{ changeNoteCurrent?.action }}
              <span v-html="changeNoteCurrent?.target?.substring(0, 30)"></span>
              <span v-if="changeNoteCurrent?.target?.length > 30">...</span>
            </span>
          </v-tooltip>

          <v-tooltip location="top" open-delay="500">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :disabled="!laterVersionExists || workflowStatusCompleted"
                variant="tonal"
                icon="mds:replay"
                class="mr-2 flip-icon-horizontal captures-description"
                @click="redoDebounced(workflow)"
              />
            </template>
            <span
            >Redo: {{ changeNoteRedo?.userRealName }}
              {{ changeNoteRedo?.action }}
              <span v-html="changeNoteRedo?.target?.substring(0, 30)"></span>
              <span v-if="changeNoteRedo?.target?.length > 30">...</span>
            </span>
          </v-tooltip>

          <div class="btn-group-divider mr-2"></div>
        </template>

        <v-menu :close-on-content-click="true">
          <template v-slot:activator="{ props: menuProps }">
            <v-tooltip
              location="top"
              open-delay="500"
              :style="{opacity: menuProps['aria-expanded'] === 'true'? 0: 1}"
            >
              <template v-slot:activator="{ props: tooltipProps }">
                <div class="flex-shrink-1 flex-grow-0" v-bind="tooltipProps">
                  <v-btn
                    :disabled="!releases?.length"
                    v-bind="menuProps"
                    variant="tonal"
                    class="mr-2"
                    :class="{hasSelected: !!showReleaseNo?.length}"
                  >
                    Filter&nbsp;<span v-if="showReleaseNo?.length">({{ showReleaseNo.length }})</span>
                    <v-icon class="ml-1">mds:filter-list</v-icon>
                  </v-btn>
                </div>
              </template>
              <span
                v-if="!releases?.length">Assign cards to releases, then filter on one or several releases</span>
              <span v-else>View the content included in one or several releases</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in releases"
              :key="index"
              :value="index"
              :class="{releaseFilter: true, 'active': showReleaseNo?.includes(item.value)}"
              @click="toggleReleaseFilter(item)"
            >
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <div class="btn-group-divider mr-2"></div>

        <v-menu>
          <template v-slot:activator="{ props: menuProps }">
            <v-tooltip location="top" open-delay="500"
                       :style="{opacity: menuProps['aria-expanded'] === 'true' ? 0 : 1}">
              <template v-slot:activator="{ props: tooltipProps }">
                <v-btn
                  variant="tonal"
                  v-bind="{ ...tooltipProps, ...menuProps }"
                >
                  Legend
                  <v-icon class="ml-1">mds:expand-more</v-icon>
                </v-btn>
              </template>
              <span>View the legend for the workflow</span>
            </v-tooltip>
          </template>

          <diagram-legend class="mt-4"/>
        </v-menu>

      </div>

      <div class="btn-group mr-2">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="tonal"
              icon="mds:crisis-alert"
              :active="sidePanelTab === 'goals' && showSidePanel"
              @click="() => openSidePanelTab('goals')"
            />
          </template>
          <span>Workflow goals</span>
        </v-tooltip>
      </div>

      <div v-if="workflow.chatLogEnabled" class="btn-group">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="tonal"
              icon="mds:temp-preferences-custom"
              :active="sidePanelTab === 'chat-log' && showSidePanel"
              @click="() => openSidePanelTab('chat-log')"
            />
          </template>
          <span>Chat log (BETA)</span>
        </v-tooltip>
      </div>
    </div>

    <div class="d-flex justify-end" style="flex: 1;" v-if="subscriptionAccessType === 'FULL'">
      <div class="btn-group">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props: tooltipActivator }">
            <v-menu transition="slide-y-transition" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="{ ...props, ...tooltipActivator }"
                  variant="tonal"
                  class="mr-2"
                  icon="mds:cloud-download"
                />
              </template>
              <v-list>
                <v-list-subheader>Export options:</v-list-subheader>
                <v-list-item :disabled="!workflow.eventsJson || !workflow.eventsJson.length" @click="exportCSV(workflow.id)">
                  <v-list-item-title>Export CSV</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="!workflow.eventsJson || !workflow.eventsJson.length" @click="exportJson(workflow.id)">
                  <v-list-item-title>Export JSON</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportWorkflow" v-if="workflow && workflow.lanes && workflow.lanes.length">
                  <v-list-item-title>Export Workflow</v-list-item-title>
                </v-list-item>
                <v-list-item @click="importWorkflow" v-else>
                  <v-list-item-title>Import Workflow</v-list-item-title>
                  <input
                    data-cy="import-workflow-input"
                    type="file"
                    ref="fileInput"
                    accept=".qlerify"
                    style="display: none"
                    @change="processWorkflow"
                  />
                </v-list-item>
                <v-list-item :disabled="!workflow.eventsJson || !workflow.eventsJson.length" @click="exportToPdf()">
                  <v-list-item-title>Export PDF</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportApplicon" v-if="workflow && workflow.canAppliconExport">
                  <v-list-item-title>Export Applicon</v-list-item-title>
                </v-list-item>

                <workflow-export-requirements-dialog
                  :requirements="eventRequirements"
                >
                </workflow-export-requirements-dialog>
              </v-list>
            </v-menu>
          </template>
          <span>Export</span>
        </v-tooltip>

        <v-tooltip v-if="!$store.state.workflow.isViewOnlyEnabled" location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="tonal"
              icon="mds:group_add"
              class="mr-2"
              @click="openShareDialog = true"
            />
          </template>
          <span>Share workflow</span>
        </v-tooltip>

        <v-tooltip v-if="!$store.state.workflow.isViewOnlyEnabled" location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="tonal"
              icon="mds:settings"
              @click="showPreferredDialog"
            />
          </template>
          <span>Workflow settings</span>
        </v-tooltip>

        <v-dialog v-model="exportCsvDialog" v-on:keydown.stop.esc="closeExportCsvDialog">
          <v-card>
            <workflow-export-csv
              ref="exportCsv"
              :workflow="workflow"
              :show-done="showDone"
              @closeExportCsvDialog="closeExportCsvDialog"
            />
          </v-card>
        </v-dialog>

        <v-dialog v-model="exportJsonDialog" v-on:keydown.stop.esc="closeExportJsonDialog">
          <v-card>
            <workflow-export-json
              ref="exportJson"
              :workflow="workflow"
              @closeExportJsonDialog="closeExportJsonDialog"
            />
          </v-card>
        </v-dialog>
      </div>
    </div>

    <div
      class="floating-side-panel-button"
      :class="{'floating-side-panel-button-hidden': showSidePanel}"
    >
      <v-tooltip location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            variant="tonal"
            color="#1F2833"
            icon="mds:right_panel_open"
            :active="['cards', 'dataFields', 'fileUpload'].includes(sidePanelTab) && showSidePanel"
            @click="() => (['cards', 'dataFields', 'fileUpload'].includes(sidePanelTab) && showSidePanel) ? closeSidePanel() : openSidePanelTab()"
          />
        </template>
        <span>Open side panel</span>
      </v-tooltip>
    </div>

    <workflow-share-dialog
      :is-share-enabled="isShareEnabled()"
      :open-share-dialog="openShareDialog"
      :workflow="workflow"
      @close="closeShareDialog"
    />

    <disclaimer-dialog
      ref="disclaimerDialog"
      @conditions-accepted="onConditionsAccepted()"
    />

    <workflow-settings-dialog
      :workflow="workflow"
      :open="workflowSettingsOpen"
      :tab-id="workflowSettingsTab"
      :disabled="workflowStatusCompleted"
      @close="closeWorkflowSettings"
    ></workflow-settings-dialog>

  </div>
</template>

<script>
import workflowApi from '@/api/workflowApi'
import WorkflowExportCsv from '@/components/WorkflowExportCsv'
import WorkflowExportJson from '@/components/WorkflowExportJson'
import WorkflowExportRequirementsDialog from '@/components/WorkflowExportRequirementsDialog'
import WorkflowShareDialog from '@/components/WorkflowShareDialog.vue'
import { EventBus } from '@/event-bus/event-bus.js'
import WorkflowSettingsDialog from '@/components/WorkflowSettingsDialog'
import DiagramLegend from '@/components/DiagramLegend'
import DisclaimerDialog from '@/components/DisclaimerDialog'
import { workflowHelpers } from '@/mixins/workflowHelpers.js'
import { mapActions, mapGetters, mapState } from 'vuex'
import { cloneAndPrepareSvg, exportDiagramAsPdf } from '@/utils/diagramPdfExport'
import randomUUID from '@/utils/uuid'
import { compress, decompress } from '@/utils/compression.js'
import { encryptTextWithPassword, decryptTextWithPassword } from '@/utils/encryption.js'
import { readFile, downloadFile } from '@/utils/fsUtils.js'
import { sanitizeCardDescription } from '@/utils/sanitize'

export default {
  mixins: [workflowHelpers],
  components: {
    WorkflowExportCsv,
    WorkflowExportJson,
    WorkflowExportRequirementsDialog,
    WorkflowShareDialog,
    WorkflowSettingsDialog,
    DiagramLegend,
    DisclaimerDialog
  },
  props: ['previousVersionExists', 'versions', 'releases', 'showDone'],

  data () {
    return {
      exportCsvDialog: false,
      exportJsonDialog: false,
      openShareDialog: false,
      workflowSettingsOpen: false,
      aiDisclaimerPreference: localStorage.getItem(`showAiInfo-${this.$route.params.projectId}`) === 'true',
      workflowSettingsTab: null
    }
  },
  methods: {
    ...mapActions({
      debounceUpdateWorkflow: 'debounceUpdateWorkflow/debounceUpdateWorkflow'
    }),
    undoDebounced (workflow) {
      this.debounceUpdateWorkflow({ fn: workflowApi.undo, args: [workflow] })
    },
    redoDebounced (workflow) {
      this.debounceUpdateWorkflow({ fn: workflowApi.redo, args: [workflow] })
    },
    openSidePanelTab (tab) {
      if (this.sidePanelTab === tab) {
        this.$store.commit('showSidePanel', !this.showSidePanel)
      } else {
        this.$store.commit('setSidePanelTab', tab ||
          (['cards', 'dataFields', 'fileUpload'].includes(this.sidePanelTab) ? this.sidePanelTab : 'cards'))
        this.$store.commit('showSidePanel', true)
      }
    },
    closeSidePanel () {
      this.$store.commit('showSidePanel', false)
    },
    exportCSV () {
      this.$nextTick(() => {
        this.exportCsvDialog = true
      })
    },
    exportJson () {
      this.$nextTick(() => {
        this.exportJsonDialog = true
        // this.$refs.exportCsv.refreshQuery()
      })
    },
    exportToPdf () {
      const flowChartOriginal = document.getElementById('flowchart')
      const workflowData = {
        svgLanesData: this.svgLanesData,
        roleDefinition: this.workflow.roleDefinition,
        hasGroups: this.hasGroups
      }

      const svgElement = cloneAndPrepareSvg(flowChartOriginal, workflowData)

      const workflowName = this.workflow.name
      exportDiagramAsPdf(svgElement, workflowName)
    },
    closeExportCsvDialog () {
      this.exportCsvDialog = false
    },
    closeExportJsonDialog () {
      this.exportJsonDialog = false
    },
    showPreferredDialog () {
      this.workflowSettingsOpen = false
      this.$refs.disclaimerDialog.openDialog()
    },
    closeShareDialog () {
      this.openShareDialog = false
    },
    isShareEnabled () {
      const res = !this.$store.state.workflow.isViewOnlyEnabled && this.project?.organisation?.userId === this.$store.state.userId
      return res
    },
    onConditionsAccepted () {
      this.workflowSettingsOpen = true
    },
    closeWorkflowSettings () {
      this.workflowSettingsTab = null
      this.workflowSettingsOpen = false
    },
    openTypesSettings (tab = 'CARDS') {
      this.workflowSettingsTab = tab
      this.workflowSettingsOpen = true
    },
    toggleReleaseFilter (item) {
      let releases = []
      if (item) {
        if (Array.isArray(this.showReleaseNo)) {
          releases = this.showReleaseNo
        }
        const value = item.value
        if (releases.includes(value)) {
          releases = releases.filter(releaseValue => releaseValue !== value)
        } else {
          releases = [...releases, value]
        }
        this.$store.commit('workflow/setShowReleaseNo', releases)
      }
    },
    async exportWorkflow () {
      // Get all bounded contexts and their objects
      if (this.workflow.boundedContexts) {
        await Promise.all(this.workflow.boundedContexts.map(async (bc) => {
          if (!this.boundedContexts[bc.id]) {
            return this.$store.dispatch('boundedContexts/getObjectsFromServer', bc.id)
          }
        }))
      }

      const workflowData = JSON.stringify({
        workflow: this.workflow,
        requirementTypes: this.requirementTypes,
        boundedContexts: this.boundedContexts
      })
      const compressedFileContent = compress(workflowData)
      const encryptedFileContent = await encryptTextWithPassword('qlerify', compressedFileContent)

      downloadFile(this.workflow.name + '.qlerify', 'application/json', JSON.stringify(encryptedFileContent))
    },
    importWorkflow () {
      this.$refs.fileInput.click()
    },
    processWorkflow (event) {
      const file = event.target.files[0]
      if (!file) {
        console.error('No file selected.')
        return
      }

      readFile(file, async (data) => {
        const decryptedFileContent = await decryptTextWithPassword('qlerify', JSON.parse(data))
        const workflowData = decompress(decryptedFileContent)
        const { workflow, requirementTypes, boundedContexts } = JSON.parse(workflowData)

        const updatedWorkflow = {
          ...workflow,
          id: this.workflow.id,
          projectId: this.workflow.projectId,
          name: this.workflow.name,
          boundedContexts: workflow.boundedContexts || []
        }

        this.uploadWorkflow(file.name, updatedWorkflow, requirementTypes || [], boundedContexts || [])
      })
    },
    async uploadWorkflow (fileName, workflow, requirementTypes, boundedContexts) {
      this.$loadingState.start('Importing workflow...')
      await this.$store.commit('workflow/setLoadingUpdate', true)

      this.$loadingState.start('Removing existing requirement types...')
      await this.deleteRequirementTypes(this.requirementTypes)

      this.$loadingState.start('Creating new requirement types...')
      const newRTIds = await this.createNewRequirementTypes(requirementTypes)

      // Set workflow basic structure
      this.$loadingState.start('Intiailizing workflow...')
      const newBoundedContexts = []
      const newBCIds = await Promise.all(workflow.boundedContexts.map(async (bc) => {
        const newId = randomUUID()
        newBoundedContexts.push({ id: newId, name: bc.name })
        return { oldId: bc.id, newId }
      }))
      const { eventsJson, newRequirementJsons } = await this.initializeWorkflow(workflow, fileName, newRTIds, newBCIds, newBoundedContexts)

      // Adding events to workflow
      this.$loadingState.start('Adding events to workflow...')
      await this.addEvents(eventsJson)

      // Adding requirements to events
      this.$loadingState.start('Adding requirements to events...')
      await this.addRequirementsToEvent(newRequirementJsons)

      // Create bounded contexts
      this.$loadingState.start('Adding bounded contexts to workflow...')
      await this.addBoundedContexts(boundedContexts, newBCIds)

      await this.$store.commit('workflow/setLoadingUpdate', false)
      this.$loadingState.stop()
    },
    exportApplicon () {
      const entities = (this.workflow.dataModels || []).map(entity => ({
        name: entity.name,
        attributes: entity.dataFields.map(field => ({
          name: field.name,
          type: field.type || 'string',
          isKey: field.primaryKey,
          required: field.tags?.includes('REQUIRED') || false
        }))
      }))
      const commands = (this.workflow.eventsJson || []).flatMap(event => {
        return (event.requirementsJson || [])
          .filter(card => !!card.cardType?.triggerType)
          .map(card => sanitizeCardDescription(card.description))
      })

      const data = { entities, commands }

      downloadFile('applicon.json', 'application/json', JSON.stringify(data, null, 2))
    }
  },
  computed: {
    ...mapGetters({
      project: 'projectMemberships/activeProject',
      svgLanesData: 'svgGraphData/svgLanesData'
    }),
    ...mapState({
      user: state => state.user.user,
      workflow: state => state.workflow.workflow,
      requirementTypes: state => state.requirementTypes.requirementTypes,
      boundedContexts: state => state.boundedContexts.boundedContexts,
      showReleaseNo: state => state.workflow.showReleaseNo,
      sidePanelTab: state => state.sidePanelTab,
      showSidePanel: state => state.showSidePanel,
      subscriptionAccessType: state => state.payments.subscriptionAccessType
    }),
    changeNoteCurrent () {
      if (!this.workflow?.sameAsVersion) {
        return this.workflow?.updateNote
      } else {
        const index = this.versions?.findIndex(element => element.version === this.workflow.sameAsVersion)
        return this.versions?.[index]?.updateNote
      }
    },
    changeNoteRedo () {
      if (!this.workflow?.sameAsVersion) {
        return {
          userRealName: '',
          action: '',
          target: ''
        }
      } else {
        const index = this.versions?.findIndex(element => element.version === (this.workflow.sameAsVersion + 1))
        return this.versions?.[index]?.updateNote || ''
      }
    },
    laterVersionExists () {
      if (this.workflow?.sameAsVersion &&
        this.versions?.findIndex(element => element.version === (this.workflow.sameAsVersion + 2)) >= 0) {
        return true
      } else {
        return false
      }
    },
    hasGroups () {
      return this.workflow?.groups && this.workflow.groups.length
    },
    eventRequirements () {
      const events = this.workflow?.eventsJson
      let requirements = []
      if (!Array.isArray(events)) {
        return requirements
      }
      for (const event of events) {
        if (event.requirementsJson) {
          requirements = requirements.concat(event.requirementsJson)
        }
      }
      return requirements
    }
  },
  watch: {
    releases (newVal) {
      if (!this.showReleaseNo) return
      // filter out selected releases that no longer exists
      const showReleaseNo = this.showReleaseNo.filter(release => {
        return newVal.findIndex(item => item.value === release) !== -1
      })
      this.$store.commit('workflow/setShowReleaseNo', showReleaseNo)
    }
  },
  async mounted () {
    EventBus.$on('open-types-settings', (tab) => {
      this.openTypesSettings(tab)
    })
    EventBus.$on('open-settings', (isTrue) => {
      this.workflowSettingsOpen = isTrue
    })
    // Next line prevents the release selector from being populated by data from different workflow
    // when the user navigates to workflow page
    this.$store.commit('workflow/setShowReleaseNo', null)
  },
  beforeUnmount () {
    EventBus.$off('open-types-settings')
    EventBus.$off('open-settings')
  }
}
</script>

<style scoped>
.btn-group {
  display: flex;
  padding: 4px 12px;
  border-radius: 8px;
  background-color: var(--light-1);
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15);
}

.btn-group-divider {
  margin: 5px 0;
  border-left: 1px solid #F1F1F1;
}

.flip-icon-horizontal {
  transform: scaleX(-1);
}

.hasSelected {
  background-color: var(--light-2) !important;
}

:deep(.floating-side-panel-button) {
  right: 0;
  top: 108px;
  z-index: 1;
  position: fixed;
  overflow: hidden;
  border-radius: 8px 0 0 8px;
  transition: 0.2s 0.1s right ease;
  background-color: var(--blue-2, #C3DAF6);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);

  .v-btn {
    width: 40px !important;
    height: 40px !important;
    background-color: var(--blue-2, #C3DAF6);
  }

  &.floating-side-panel-button-hidden {
    right: -100%;
  }
}
</style>
